import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  Filler
} from 'chart.js'
import 'chartjs-adapter-moment'
import moment from 'moment'
import { useTonightSunsetSunrise } from '../../../../hooks/useTonightSunsetSunrise'
import { IconButton, Typography, useTheme } from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'

ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  ChartTooltip,
  ChartLegend,
  Filler
)

// Devuelve entre que dos thresholds cae un valor
const getThresholdBand = (value, thresholds) => {
  const sorted = (thresholds || [])
    .filter(line => typeof line.value === 'number')
    .slice()
    .sort((a, b) => a.value - b.value)
  if (sorted.length === 0) return null

  const upper = sorted.find(line => line.value > value) || null
  const lowerCandidates = sorted.filter(line => line.value <= value)
  const lower = lowerCandidates.length ? lowerCandidates[lowerCandidates.length - 1] : null

  return { lower, upper }
}

// Texto para el tooltip
const formatThresholdBand = (value, thresholds) => {
  const band = getThresholdBand(value, thresholds)
  if (!band) return null
  const name = line => line.label || `${line.value}`
  const { lower, upper } = band
  if (lower && upper) return `Franja: ${name(lower)} - ${name(upper)}`
  if (lower) return `Franja: ${name(lower)}`
  if (upper) return `Franja: ${name(upper)}`
  return null
}

//Opciones para el grafico:
/*
  showGrid: boolean -> muestra una rejilla de fondo con líneas verticales y horizontales
  pointGrid: boolean -> muestra una rejilla que pasa por cada punto de medida
  hourTickByData: boolean -> muestra una marca de tiempo en el eje X por cada medida (en vez de auto)
  height: number -> altura fija del gráfico en pixeles (si no se da, el gráfico ajusta su altura automáticamente)
*/
export const CmSensorMeasuresChart = ({
  activeCmSensor,
  measures,
  startHour,
  endHour,
  centerOn,
  legend,
  todaySunriseSunset,
  baseDate,
  showGrid,
  pointGrid,
  hourTickByData, //si quiero una marca de tiempo por cada medida
  height, //para aumentar tamaño vertical solo si queremos, es opcional
  horizontalLines, // array de objetos {value, color, label} para dibujar lineas horizontales de referencia
  showAdaptativeThresholds, //boolean, si true, dibuja lineas horizontales de referencia de los valores de adaptativo del contrato
  thresholdLabelMode, // inline | legend | auto en modo auto si hay lineas muy juntas pone leyenda
  legendPosition,  // top-left | top-right | bottom-left | bottom-right
  legendScale,
  yScaleMax, //opcional, para forzar un maximo en el eje Y
}) => {
  // Hooks y datos de Redux
  const theme = useTheme()

  // ocultar/mostrar la leyenda
  const [legendHidden, setLegendHidden] = useState(false)
  const toggleLegend = () => setLegendHidden(h => !h)

  const [legendBoxActive, setLegendBoxActive] = useState(false)
  const syncLegendBoxActive = (active) => {
    setLegendBoxActive(prev => (prev === active ? prev : active))
  }

  const { sunrise, sunset } = useTonightSunsetSunrise(todaySunriseSunset)
  // Hora exacta del punto de referencia (orto u ocaso)
  const centerTime = centerOn === 'sunrise'
    ? moment(sunrise)
    : moment(sunset)

  if (!centerTime || !centerTime.isValid()) {
    return null
  }

  // Rango del eje X, calculado a partir del día clickado y los props startHour/endHour
  const referenceDate = baseDate ? moment(baseDate) : moment(centerTime)  //para el calculo del orto/ocaso de ese dia
  const startTime = moment(referenceDate).set({ hour: startHour, minute: 0, second: 0, millisecond: 0 })
  const endTime = moment(referenceDate).set({ hour: endHour, minute: 0, second: 0, millisecond: 0 })

  // Si el rango cruza medianoche, el fin pasa al día siguiente
  if (endHour < startHour) {
    endTime.add(1, 'day')
  }

  // Ajustamos la hora de referencia al día del baseDate
  // (el sunrise/sunset puede venir de otro día distinto)
  const adjustedCenterTime = moment(referenceDate)
    .set({
      hour: centerTime.hour(),
      minute: centerTime.minute(),
      second: centerTime.second()
    })

  if (endHour < startHour && adjustedCenterTime.hour() < startHour) {
    adjustedCenterTime.add(1, 'day')
  }
  // Transformamos las medidas en puntos {x, y} para el gráfico
  const measurePoints = (measures || [])
    .filter(measure => measure && measure.measure_date && measure.measure_value !== undefined)
    .map(measure => ({
      x: moment(measure.measure_date),
      y: measure.measure_value
    }))
    .filter(point => point.x.isSameOrAfter(startTime) && point.x.isSameOrBefore(endTime))
    .sort((firstPoint, secondPoint) => firstPoint.x - secondPoint.x)

  // Si no hay medidas en esta franja, no dibujamos el gráfico
  if (measurePoints.length === 0) {
    return (
      <Typography variant='caption' display='block' gutterBottom>
        {`No hay medidas en la franja del ${centerOn === 'sunrise' ? 'orto' : 'ocaso'}`}
      </Typography>
    )
  }
  const axisMin = measurePoints.length > 0
    ? moment(measurePoints[0].x).subtract(measurePoints.length === 1 ? 30 : 0, 'minutes').toDate()
    : startTime.toDate()
  const axisMax = measurePoints.length > 0
    ? moment(measurePoints[measurePoints.length - 1].x).add(measurePoints.length === 1 ? 30 : 0, 'minutes').toDate()
    : endTime.toDate()

  const color = theme.palette.primary.main

  // Configuración visual de la línea y los puntos
  const processedData = {
    datasets: [
      {
        label: 'Medidas',
        fill: true,
        tension: 0,
        backgroundColor: `${color}70`,
        borderColor: color,
        pointBackgroundColor: color,
        pointBorderColor: color,
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        data: measurePoints
      }
    ]
  }

  const fixedHeight = typeof height === 'number' ? height : null
  // Configuración de los ejes del gráfico
  const options = {
    responsive: true,
    maintainAspectRatio: fixedHeight ? false : true,
    layout: {
      padding: theme.spacing(1)
    },
    plugins: {
      legend: {
        display: legend
      },
      tooltip: {
        callbacks: {
          label: (context) => `Medida: ${context.parsed.y} lx`,
          afterLabel: (context) => formatThresholdBand(context.parsed.y, horizontalLines) || ''
        }
      }
    },
    scales: {
      // Eje X (horas)
      x: {
        type: 'time',
        grid: { display: showGrid, color: 'rgba(0,0,0,0.08)' },
        time: {
          tooltipFormat: 'HH:mm',
          unit: 'minute',
          stepSize: 1,
          displayFormats: {
            millisecond: 'HH:mm',
            second: 'HH:mm',
            minute: 'HH:mm',
            hour: 'HH:mm',
            day: 'HH:mm'

          }
        },
        ticks: {
          maxRotation: 90,
          minRotation: 45,
          autoSkip: true,
          source: hourTickByData ? 'data' : 'auto' //para que salga una por cada medida, quitar si quiero que sea como antes
        },
        min: axisMin,
        max: axisMax,
        display: true
      },
      // Eje Y (valor de las medidas)
      y: {
        grid: { display: showGrid, color: 'rgba(0,0,0,0.08)' },
        beginAtZero: true,
        max: typeof yScaleMax === 'number' ? yScaleMax : undefined,
        display: true
      }
    }
  }

  // Plugin que dibuja la línea vertical de referencia (orto u ocaso)
  const referenceLine = {
    id: 'referenceLine',
    afterDatasetsDraw(chart) {
      const ctx = chart.ctx
      const xScale = chart.scales.x
      const yScale = chart.scales.y

      if (!xScale || !yScale) return

      // Posición en pixeles donde cae la hora de referencia
      const timestamp = adjustedCenterTime.valueOf()
      const x_point = xScale.getPixelForValue(timestamp)

      // Si cae fuera del área del gráfico, no dibujamos nada
      if (x_point < xScale.left || x_point > xScale.right) {

        return
      }

      const topY = yScale.top
      const bottomY = yScale.bottom

      // Dibujar la línea vertical
      ctx.save()
      ctx.beginPath()
      ctx.moveTo(x_point, topY)
      ctx.lineTo(x_point, bottomY)
      ctx.lineWidth = 2
      ctx.strokeStyle = theme.palette.secondary.main
      ctx.stroke()

      // Etiqueta encima de la línea
      ctx.font = '14px Arial'
      ctx.fillText(centerOn === 'sunrise' ? '🌞' : '🌚', x_point + 4, topY + 14)
      ctx.restore()
    }
  }
  //plugin de grid
  // Dibuja, por cada punto, una linea vertical y una horizontal
  const pointGridLines = {
    id: 'pointGridLines',
    afterDatasetsDraw(chart) {
      const ctx = chart.ctx
      const xScale = chart.scales.x
      const yScale = chart.scales.y
      if (!xScale || !yScale) return

      const meta = chart.getDatasetMeta(0)
      if (!meta || !meta.data) return

      ctx.save()
      ctx.strokeStyle = 'rgba(0,0,0,0.15)'
      ctx.lineWidth = 1
      //ctx.setLineDash([3, 3]) //para lineas discontinuas

      meta.data.forEach(element => {
        const { x, y } = element.getProps(['x', 'y'], true) // posición en píxeles del punto

        // vertical
        ctx.beginPath()
        ctx.moveTo(x, yScale.top)
        ctx.lineTo(x, yScale.bottom)
        ctx.stroke()

        // horizontal
        ctx.beginPath()
        ctx.moveTo(xScale.left, y)
        ctx.lineTo(xScale.right, y)
        ctx.stroke()
      })

      ctx.restore()
    }
  }

  //plugin de lineas horizontales que representan los thresholds de referencia
  const horizontalReferenceLines = {
    id: 'horizontalReferenceLines',
    afterDatasetsDraw(chart) {
      const ctx = chart.ctx
      const xScale = chart.scales.x
      const yScale = chart.scales.y
      if (!xScale || !yScale) return

      const lines = horizontalLines || []
      if (lines.length === 0) {
        syncLegendBoxActive(false);
        return
      }

      ctx.save()

      //Dibujo de lineas
      const visibleLines = []
      lines.forEach(line => {
        const y = yScale.getPixelForValue(line.value)
        if (y < yScale.top || y > yScale.bottom) return
        visibleLines.push({ ...line, y })

        ctx.beginPath()
        ctx.moveTo(xScale.left, y)
        ctx.lineTo(xScale.right, y)
        ctx.lineWidth = 2
        ctx.strokeStyle = line.color || theme.palette.error.main
        ctx.setLineDash([9, 6])
        ctx.stroke()
      })
      ctx.setLineDash([])

      // labels visibles dentro del rango
      const labeled = visibleLines
        .filter(line => line.label)
        .sort((a, b) => a.y - b.y)

      if (labeled.length === 0) {
        syncLegendBoxActive(false)
        ctx.restore()
        return
      }

      const LABEL_GAP = 14 // separacion minima entre labels para que se lean
      ctx.font = '12px Arial'

      ctx.textBaseline = 'middle' //para que salga en medio de la linea y no encima

      // ¿Se permite el recuadro de leyenda? (thresholdLegendMode: 'auto' | 'show' | 'hide')
      // Decidir recuadro de leyenda vs etiquetas inline.
      // Prioridad: modo inline forzado > boton (hide/show) > modo legend forzado > auto por solapamiento
      let usingLegend
      if (thresholdLabelMode === 'inline') usingLegend = false
      else if (thresholdLabelMode === 'legend') usingLegend = true
      else {
        // auto: si dos lineas estan mas cerca que el gap, se usa leyenda
        usingLegend = labeled.some((currentLine, index) => {
          if (index === 0) return false // la primera no se compara
          const previousLine = labeled[index - 1]
          const distance = currentLine.y - previousLine.y
          return distance < LABEL_GAP
        })
      }
      syncLegendBoxActive(usingLegend)
      // para boton de mostrar o no la leyenda
      // si el modo es leyenda y el usuario la ha ocultado, no dibujamos el
      // recuadro.
      if (usingLegend && legendHidden) {
        ctx.restore()
        return
      }

      // inline,dibuja la etiqueta sobre cada linea,
      if (!usingLegend) {
        const side = centerOn === 'sunrise' ? 'left' : 'right'
        labeled.forEach(item => {
          const textWidth = ctx.measureText(item.label).width
          const x = side === 'right'
            ? xScale.right - 6 - textWidth
            : xScale.left + 6
          ctx.fillStyle = 'rgba(255,255,255,0.8)'
          ctx.fillRect(x - 3, item.y - 7, textWidth + 6, 14)
          ctx.fillStyle = item.color || theme.palette.error.main
          ctx.fillText(item.label, x, item.y)
        })
        ctx.restore()
        return
      }
      ctx.font = `${12 * legendScale}px Arial`

      //leyenda recuadro en una esquina
      const leyendPadding = 8 * legendScale
      const leyendRowHeight = 16 * legendScale
      const leyendColorSampleWidth = 6.5 * legendScale
      const leyendTextGap = 6 * legendScale
      const leyendMargin = 8

      const maxTextWidth = Math.max(...labeled.map(line => ctx.measureText(line.label).width))
      const boxWidth = leyendPadding * 2 + leyendColorSampleWidth + leyendTextGap + maxTextWidth
      const boxHeight = leyendPadding * 2 + labeled.length * leyendRowHeight

      //posicion del recuadro
      const cornerPosition = thresholdLabelMode === 'auto'
        ? (centerOn === 'sunrise' ? 'top-left' : 'top-right')
        : legendPosition

      let boxXPosition = xScale.left + leyendMargin
      let boxYPosition = yScale.top + leyendMargin

      if (cornerPosition === 'top-right') {
        boxXPosition = xScale.right - leyendMargin - boxWidth
      }
      // fondo del recuadro
      ctx.fillStyle = 'rgba(255,255,255,0.9)'
      ctx.fillRect(boxXPosition, boxYPosition, boxWidth, boxHeight)
      ctx.strokeStyle = 'rgba(0,0,0,0.15)'
      ctx.lineWidth = 1
      ctx.strokeRect(boxXPosition, boxYPosition, boxWidth, boxHeight)

      // dibujo de la leyenda
      labeled.forEach((item, i) => {
        const rowY = boxYPosition + leyendPadding + leyendRowHeight * i + leyendRowHeight / 2
        const swatchX = boxXPosition + leyendPadding
        const color = item.color || theme.palette.error.main

        ctx.beginPath()
        ctx.moveTo(swatchX, rowY)
        ctx.lineTo(swatchX + leyendColorSampleWidth, rowY)
        ctx.lineWidth = 2
        ctx.strokeStyle = color
        ctx.setLineDash([4, 3])
        ctx.stroke()
        ctx.setLineDash([])

        ctx.fillStyle = color
        ctx.fillText(item.label, swatchX + leyendColorSampleWidth + leyendTextGap, rowY)
      })

      ctx.restore()
    }
  }

  //!!!EL GRAFICO
  const chart = (
    <Line
      key={`${centerOn}-${baseDate ? moment(baseDate).format('YYYY-MM-DD') : 'now'}`}
      data={processedData}
      options={options}
      redraw
      plugins={pointGrid
        ? [referenceLine, pointGridLines, horizontalReferenceLines]
        : [referenceLine, horizontalReferenceLines]}
    />
  )
  const showLegendToggle = legendBoxActive
  const showLegendButtonSide = centerOn === 'sunrise' ? { left: 40 } : { right: 4 }
  // Renderizado final: contador de medidas + gráfico
  return (
    <>
      <Typography variant='caption' display='block'>
        {`${measurePoints.length} medidas`}
      </Typography>

      <div style={{ position: 'relative', width: '100%', ...(fixedHeight ? { height: fixedHeight } : {}) }}>
        {showLegendToggle && (
          <IconButton
            size='small'
            onClick={toggleLegend}
            style={{ position: 'absolute', top: 4, ...showLegendButtonSide, zIndex: 1, padding: 2 }}
          >
            {legendHidden
              ? <VisibilityOff fontSize='small' />
              : <Visibility fontSize='small' />}
          </IconButton>
        )}
        {chart}
      </div>
    </>
  )
}

CmSensorMeasuresChart.defaultProps = {
  measures: [],
  startHour: 20,
  endHour: 3,
  centerOn: 'sunset',
  legend: true,
  todaySunriseSunset: true,
  showGrid: false,
  pointGrid: false,
  hourTickByData: false,
  horizontalLines: [],
  thresholdLabelMode: 'auto',
  legendPosition: 'top-left',
  legendScale: 1,
  yScaleMax: undefined

}

CmSensorMeasuresChart.propTypes = {
  measures: PropTypes.arrayOf(
    PropTypes.shape({
      measure_date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      measure_value: PropTypes.number
    })
  ),
  startHour: PropTypes.number,
  endHour: PropTypes.number,
  centerOn: PropTypes.oneOf(['sunset', 'sunrise']),
  legend: PropTypes.bool,
  todaySunriseSunset: PropTypes.bool,
  baseDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  showGrid: PropTypes.bool,
  pointGrid: PropTypes.bool,
  hourTickByData: PropTypes.bool,
  horizontalLines: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      color: PropTypes.string,
      label: PropTypes.string
    })
  ),
  height: PropTypes.number,
  thresholdLabelMode: PropTypes.oneOf(['auto', 'inline', 'legend']),
  legendPosition: PropTypes.oneOf(['top-left', 'top-right', 'bottom-left', 'bottom-right']),
  legendScale: PropTypes.number,
  yScaleMax: PropTypes.number
}